import type { BlockyTimeDB } from '@/db/schema'
import { db as defaultDb } from '@/db/schema'
import type { EntryRow } from '@/db/types'
import type { BlockGrid, BlockSlot } from '@/core/time/blockGrid'
import { getDeviceId } from './syncStateRepository'

/**
 * THE ONLY WRITER OF THE `entries` TABLE.
 *
 * No component, no other service. Two invariants are enforced here and
 * nowhere else, and both are unrecoverable if broken:
 *
 *   1. AT MOST ONE LIVE ENTRY PER (dayKey, slotIndex).
 *      Not enforced by an index, deliberately: sync can legitimately deliver a
 *      duplicate that must be REPAIRED rather than rejected. A unique index
 *      would make the pull fail instead of converging.
 *
 *   2. EVERY WRITE SETS updatedAt, deviceId AND _dirty.
 *      Miss any one and the row silently never syncs -- no error, no symptom,
 *      until you notice months later that one device is missing a week.
 *
 * Deletes are soft. A hard delete cannot propagate, so the other device would
 * never learn of it and would push the row straight back.
 */

export interface EntryContent {
  eventTypeId: string | null
  eventObjectId: string | null
  comment: string | null
  isPlanned: boolean
}

/** Before/after snapshot, so Phase 5's undo can invert any operation. */
export interface ChangeSet {
  before: EntryRow[]
  after: EntryRow[]
}

export interface UpsertOptions {
  /** Leave already-filled blocks untouched. */
  skipOccupied?: boolean
}

const EMPTY_CHANGE: ChangeSet = { before: [], after: [] }

function isLive(row: EntryRow | undefined): row is EntryRow {
  return row !== undefined && row.deletedAt === null
}

/** The live entry at a slot, or undefined. */
export async function entryAt(
  slot: BlockSlot,
  db: BlockyTimeDB = defaultDb,
): Promise<EntryRow | undefined> {
  const rows = await db.entries
    .where('[dayKey+slotIndex]')
    .equals([slot.dayKey, slot.slotIndex])
    .toArray()

  return rows.find(isLive)
}

/** Live entries for a day range, inclusive. Drives the timeline. */
export async function entriesForDayRange(
  loDayKey: number,
  hiDayKey: number,
  db: BlockyTimeDB = defaultDb,
): Promise<EntryRow[]> {
  const rows = await db.entries.where('dayKey').between(loDayKey, hiDayKey, true, true).toArray()
  return rows.filter(isLive)
}

/**
 * Marks slots with the given content.
 *
 * Never blind-inserts: an occupied slot is UPDATED IN PLACE, keeping its id.
 * Reusing the id is what stops overwriting a block from producing two rows for
 * the same minute, both of which would then sync.
 */
export async function upsert(
  grid: BlockGrid,
  content: EntryContent,
  slots: readonly BlockSlot[],
  options: UpsertOptions = {},
  db: BlockyTimeDB = defaultDb,
): Promise<ChangeSet> {
  if (slots.length === 0) return EMPTY_CHANGE

  const deviceId = await getDeviceId(db)
  const now = Date.now()

  const before: EntryRow[] = []
  const after: EntryRow[] = []

  await db.transaction('rw', db.entries, async () => {
    for (const slot of slots) {
      // Read inside the transaction. Reading first and writing after would
      // leave a window in which a concurrent write inserts a second row.
      const existingRows = await db.entries
        .where('[dayKey+slotIndex]')
        .equals([slot.dayKey, slot.slotIndex])
        .toArray()

      const live = existingRows.filter(isLive)

      if (live.length > 0 && options.skipOccupied) continue

      // Prefer the live row. Failing that, REVIVE THE NEWEST TOMBSTONE rather
      // than inserting a fresh row beside it.
      //
      // The slot is the natural identity here, not the entry. Inserting a new
      // id on every mark-erase-mark cycle would grow the table without bound
      // for a block the user keeps changing their mind about, and would leave
      // two rows for the same minute travelling through sync. Reusing the id
      // also means a concurrent erase on one device and mark on the other
      // resolve by last-write-wins instead of both surviving.
      const survivor =
        live[0] ??
        (existingRows.length > 0
          ? existingRows.reduce((newest, row) =>
              row.updatedAt > newest.updatedAt ? row : newest,
            )
          : undefined)

      for (const extra of live.slice(1)) {
        before.push({ ...extra })
        const tombstone: EntryRow = {
          ...extra,
          deletedAt: now,
          updatedAt: now,
          deviceId,
          _dirty: 1,
        }
        await db.entries.put(tombstone)
        after.push(tombstone)
      }

      const row: EntryRow = survivor
        ? {
            ...survivor,
            eventTypeId: content.eventTypeId,
            eventObjectId: content.eventObjectId,
            comment: content.comment,
            isPlanned: content.isPlanned ? 1 : 0,
            deletedAt: null,
            updatedAt: now,
            deviceId,
            _dirty: 1,
          }
        : {
            id: crypto.randomUUID(),
            dayKey: slot.dayKey,
            slotIndex: slot.slotIndex,
            startAt: grid.startOfSlot(slot.dayKey, slot.slotIndex),
            durationMinutes: grid.precisionMinutes,
            eventTypeId: content.eventTypeId,
            eventObjectId: content.eventObjectId,
            comment: content.comment,
            isPlanned: content.isPlanned ? 1 : 0,
            updatedAt: now,
            deletedAt: null,
            deviceId,
            serverSeq: 0,
            _dirty: 1,
          }

      if (survivor) before.push({ ...survivor })
      await db.entries.put(row)
      after.push(row)
    }
  })

  return { before, after }
}

/**
 * Clears slots.
 *
 * Tombstones rather than deletes. The row stays, carrying deletedAt, so the
 * deletion can travel to the other device.
 */
export async function erase(
  slots: readonly BlockSlot[],
  db: BlockyTimeDB = defaultDb,
): Promise<ChangeSet> {
  if (slots.length === 0) return EMPTY_CHANGE

  const deviceId = await getDeviceId(db)
  const now = Date.now()

  const before: EntryRow[] = []
  const after: EntryRow[] = []

  await db.transaction('rw', db.entries, async () => {
    for (const slot of slots) {
      const rows = await db.entries
        .where('[dayKey+slotIndex]')
        .equals([slot.dayKey, slot.slotIndex])
        .toArray()

      for (const row of rows.filter(isLive)) {
        before.push({ ...row })
        const tombstone: EntryRow = {
          ...row,
          deletedAt: now,
          updatedAt: now,
          deviceId,
          _dirty: 1,
        }
        await db.entries.put(tombstone)
        after.push(tombstone)
      }
    }
  })

  return { before, after }
}

/** Restores a ChangeSet's `before` state. Used by undo in Phase 5. */
export async function restore(
  rows: readonly EntryRow[],
  erasedIds: readonly string[],
  db: BlockyTimeDB = defaultDb,
): Promise<void> {
  const deviceId = await getDeviceId(db)
  const now = Date.now()

  await db.transaction('rw', db.entries, async () => {
    for (const id of erasedIds) {
      const row = await db.entries.get(id)
      if (row) {
        await db.entries.put({ ...row, deletedAt: now, updatedAt: now, deviceId, _dirty: 1 })
      }
    }

    for (const row of rows) {
      await db.entries.put({ ...row, updatedAt: now, deviceId, _dirty: 1 })
    }
  })
}

/**
 * Finds slots holding more than one live entry.
 *
 * Should always return empty. Phase 4 runs this after every sync pull, where
 * duplicates genuinely can arise: two devices marking the same empty slot
 * offline generate different UUIDs, so neither push conflicts.
 */
export async function findDuplicateSlots(
  loDayKey: number,
  hiDayKey: number,
  db: BlockyTimeDB = defaultDb,
): Promise<{ slot: BlockSlot; rows: EntryRow[] }[]> {
  const live = await entriesForDayRange(loDayKey, hiDayKey, db)

  const bySlot = new Map<string, EntryRow[]>()
  for (const row of live) {
    const key = `${row.dayKey}:${row.slotIndex}`
    const list = bySlot.get(key)
    if (list) list.push(row)
    else bySlot.set(key, [row])
  }

  const out: { slot: BlockSlot; rows: EntryRow[] }[] = []
  for (const [key, rows] of bySlot) {
    if (rows.length < 2) continue
    const parts = key.split(':')
    out.push({
      slot: { dayKey: Number(parts[0]), slotIndex: Number(parts[1]) },
      rows,
    })
  }
  return out
}

/** Live entry count. For diagnostics and the storage display. */
export async function countEntries(db: BlockyTimeDB = defaultDb): Promise<number> {
  return db.entries.filter((e) => e.deletedAt === null).count()
}

/**
 * Repoints every entry from one event object to another.
 *
 * Lives here because only this module writes `entries` -- merging two objects
 * is an object-level operation, but its effect on entries has to go through
 * the one guarded path. Returns a ChangeSet so a merge is undoable in one step.
 */
export async function reassignObject(
  fromObjectId: string,
  toObjectId: string | null,
  db: BlockyTimeDB = defaultDb,
): Promise<ChangeSet> {
  const deviceId = await getDeviceId(db)
  const now = Date.now()

  const before: EntryRow[] = []
  const after: EntryRow[] = []

  await db.transaction('rw', db.entries, async () => {
    const affected = await db.entries.where('eventObjectId').equals(fromObjectId).toArray()

    for (const row of affected) {
      before.push({ ...row })
      const updated: EntryRow = {
        ...row,
        eventObjectId: toObjectId,
        updatedAt: now,
        deviceId,
        _dirty: 1,
      }
      await db.entries.put(updated)
      after.push(updated)
    }
  })

  return { before, after }
}

/** Repoints entries away from an event type, e.g. when merging types. */
export async function reassignType(
  fromTypeId: string,
  toTypeId: string | null,
  db: BlockyTimeDB = defaultDb,
): Promise<ChangeSet> {
  const deviceId = await getDeviceId(db)
  const now = Date.now()

  const before: EntryRow[] = []
  const after: EntryRow[] = []

  await db.transaction('rw', db.entries, async () => {
    const affected = await db.entries.where('eventTypeId').equals(fromTypeId).toArray()

    for (const row of affected) {
      before.push({ ...row })
      const updated: EntryRow = {
        ...row,
        eventTypeId: toTypeId,
        updatedAt: now,
        deviceId,
        _dirty: 1,
      }
      await db.entries.put(updated)
      after.push(updated)
    }
  })

  return { before, after }
}
